/**
 * Typed functions for every admin endpoint, built on `openapi-fetch` and the generated
 * `paths` (`./schema.d.ts`), so paths, parameters and bodies are checked against the API's
 * openapi.json. Requests go through `authFetch` (bearer token, single-flight refresh, one retry);
 * errors become `ApiError` from the problem+json body. Components use these via `./queries.ts`.
 */
import createClient from 'openapi-fetch';
import { ApiError, apiOrigin, authFetch } from './http';
import type { paths } from './schema';
import type {
  CollectionInput,
  CreateCollectionInput,
  CreateCreatorInput,
  CreateEpisodeInput,
  CreateFormatGroupInput,
  CreateProgrammeInput,
  CreateSeasonInput,
  CreateTermInput,
  CreatorInput,
  EpisodePatch,
  FormatGroupInput,
  HomeSlotInput,
  ImageKind,
  LayoutBand,
  ListParams,
  Problem,
  ProgrammeListParams,
  ProgrammePatch,
  ProgrammeStatus,
  SeasonInput,
  StaffRole,
  TaxonomyInput,
  TermListParams,
  VideoListParams,
  VideoPurpose,
} from './types';

const client = createClient<paths>({
  // openapi-fetch needs an absolute base; same-origin in dev (the Vite proxy).
  baseUrl: apiOrigin(),
  fetch: authFetch,
  headers: { Accept: 'application/json, application/problem+json' },
});

/** Unwrap an openapi-fetch result: the data, or throw `ApiError`. */
async function call<T>(p: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  const { data, error, response } = await p;
  if (!response.ok) {
    const problem =
      error && typeof error === 'object'
        ? (error as Problem)
        : { status: response.status, title: response.statusText };
    throw new ApiError(response.status, problem);
  }
  return data as T;
}

const id = (v: string) => ({ params: { path: { id: v } } });

export const adminApi = {
  // ---- staff ----
  me: () => call(client.GET('/v1/admin/me')),
  listStaff: () => call(client.GET('/v1/admin/staff')),
  inviteStaff: (body: { email: string; role: StaffRole }) =>
    call(client.POST('/v1/admin/staff/invites', { body })),
  revokeInvite: (inviteId: string) =>
    call(client.DELETE('/v1/admin/staff/invites/{id}', id(inviteId))),
  updateStaff: (staffId: string, body: { role?: StaffRole; disabled?: boolean }) =>
    call(client.PATCH('/v1/admin/staff/{id}', { ...id(staffId), body })),

  // ---- creators ----
  listCreators: (query?: ListParams) =>
    call(client.GET('/v1/admin/creators', { params: { query } })),
  getCreator: (creatorId: string) => call(client.GET('/v1/admin/creators/{id}', id(creatorId))),
  createCreator: (body: CreateCreatorInput) => call(client.POST('/v1/admin/creators', { body })),
  updateCreator: (creatorId: string, body: CreatorInput) =>
    call(client.PATCH('/v1/admin/creators/{id}', { ...id(creatorId), body })),
  archiveCreator: (creatorId: string) =>
    call(client.POST('/v1/admin/creators/{id}/archive', id(creatorId))),
  restoreCreator: (creatorId: string) =>
    call(client.POST('/v1/admin/creators/{id}/restore', id(creatorId))),

  // ---- programmes ----
  listProgrammes: (query?: ProgrammeListParams) =>
    call(client.GET('/v1/admin/programmes', { params: { query } })),
  getProgramme: (programmeId: string) =>
    call(client.GET('/v1/admin/programmes/{id}', id(programmeId))),
  createProgramme: (body: CreateProgrammeInput) =>
    call(client.POST('/v1/admin/programmes', { body })),
  updateProgramme: (programmeId: string, revision: number, body: ProgrammePatch) =>
    call(
      client.PATCH('/v1/admin/programmes/{id}', {
        params: { path: { id: programmeId }, header: { 'If-Match': String(revision) } },
        body,
      }),
    ),
  transitionProgramme: (programmeId: string, to: ProgrammeStatus) =>
    call(client.POST('/v1/admin/programmes/{id}/transition', { ...id(programmeId), body: { to } })),

  // ---- seasons & episodes ----
  createSeason: (programmeId: string, body: CreateSeasonInput) =>
    call(client.POST('/v1/admin/programmes/{id}/seasons', { ...id(programmeId), body })),
  updateSeason: (seasonId: string, body: SeasonInput) =>
    call(client.PATCH('/v1/admin/seasons/{id}', { ...id(seasonId), body })),
  deleteSeason: (seasonId: string) => call(client.DELETE('/v1/admin/seasons/{id}', id(seasonId))),
  createEpisode: (programmeId: string, body: CreateEpisodeInput) =>
    call(client.POST('/v1/admin/programmes/{id}/episodes', { ...id(programmeId), body })),
  updateEpisode: (episodeId: string, body: EpisodePatch) =>
    call(client.PATCH('/v1/admin/episodes/{id}', { ...id(episodeId), body })),
  archiveEpisode: (episodeId: string) =>
    call(client.POST('/v1/admin/episodes/{id}/archive', id(episodeId))),
  restoreEpisode: (episodeId: string) =>
    call(client.POST('/v1/admin/episodes/{id}/restore', id(episodeId))),

  // ---- taxonomy ----
  listTaxonomy: (query: TermListParams) =>
    call(client.GET('/v1/admin/taxonomy', { params: { query } })),
  createTerm: (body: CreateTermInput) => call(client.POST('/v1/admin/taxonomy', { body })),
  updateTerm: (termId: string, body: TaxonomyInput) =>
    call(client.PATCH('/v1/admin/taxonomy/{id}', { ...id(termId), body })),
  archiveTerm: (termId: string) => call(client.POST('/v1/admin/taxonomy/{id}/archive', id(termId))),
  setInterestSubjects: (termId: string, subject_ids: string[]) =>
    call(client.PUT('/v1/admin/taxonomy/{id}/subjects', { ...id(termId), body: { subject_ids } })),
  listFormatGroups: () => call(client.GET('/v1/admin/format-groups')),
  createFormatGroup: (body: CreateFormatGroupInput) =>
    call(client.POST('/v1/admin/format-groups', { body })),
  updateFormatGroup: (slug: string, body: FormatGroupInput) =>
    call(client.PATCH('/v1/admin/format-groups/{slug}', { params: { path: { slug } }, body })),
  deleteFormatGroup: (slug: string) =>
    call(client.DELETE('/v1/admin/format-groups/{slug}', { params: { path: { slug } } })),

  // ---- collections ----
  listCollections: (query?: ListParams) =>
    call(client.GET('/v1/admin/collections', { params: { query } })),
  getCollection: (collectionId: string) =>
    call(client.GET('/v1/admin/collections/{id}', id(collectionId))),
  createCollection: (body: CreateCollectionInput) =>
    call(client.POST('/v1/admin/collections', { body })),
  updateCollection: (collectionId: string, body: CollectionInput) =>
    call(client.PATCH('/v1/admin/collections/{id}', { ...id(collectionId), body })),
  setCollectionItems: (collectionId: string, programme_ids: string[]) =>
    call(
      client.PUT('/v1/admin/collections/{id}/items', {
        ...id(collectionId),
        body: { programme_ids },
      }),
    ),
  archiveCollection: (collectionId: string) =>
    call(client.POST('/v1/admin/collections/{id}/archive', id(collectionId))),

  // ---- home layouts (approver+) ----
  listHomeLayouts: () => call(client.GET('/v1/admin/home-layouts')),
  putHomeLayout: (band: LayoutBand, slots: HomeSlotInput[]) =>
    call(
      client.PUT('/v1/admin/home-layouts/{band}', {
        params: { path: { band } },
        body: { slots },
      }),
    ),

  // ---- videos & images ----
  listVideos: (query?: VideoListParams) =>
    call(client.GET('/v1/admin/videos', { params: { query } })),
  getVideo: (videoId: string) => call(client.GET('/v1/admin/videos/{id}', id(videoId))),
  createVideo: (body: { title: string; purpose: VideoPurpose }) =>
    call(client.POST('/v1/admin/videos', { body })),
  renewUpload: (videoId: string) => call(client.POST('/v1/admin/videos/{id}/upload', id(videoId))),
  linkStaticVideo: (body: { title: string; hls_url: string; purpose?: VideoPurpose }) =>
    call(client.POST('/v1/admin/videos/static', { body })),
  videoPlayback: (videoId: string) =>
    call(client.POST('/v1/admin/videos/{id}/playback', id(videoId))),
  archiveVideo: (videoId: string) =>
    call(client.POST('/v1/admin/videos/{id}/archive', id(videoId))),
  uploadImage: (file: File, kind: ImageKind) =>
    call(
      client.POST('/v1/admin/images', {
        // The generated body type describes the multipart fields; send real FormData.
        body: { file: '', kind } as never,
        bodySerializer: () => {
          const fd = new FormData();
          fd.append('kind', kind);
          fd.append('file', file);
          return fd;
        },
      }),
    ),
};

export type AdminApi = typeof adminApi;
